import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box, Grid, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';

export type AudioTimeIndicatorProps = {};

const GeneratingAnimation = lazy(() =>
	import('./GeneratingAnimation').then((mod) => ({ default: mod.GeneratingAnimation })),
);

const DEFAULT_TIME_DISPLAY = '0:00';

const styles = cssMap({
	grid: {
		gridTemplateColumns: '44px 8px 44px',
		width: 'fit-content',
		minHeight: '20px',
	},
	codeStyledText: {
		fontFamily: token('font.family.code'),
		color: token('color.text.subtlest'),
	},
});

const formatTime = (timeInSeconds: number): string => {
	const minutes = Math.floor(timeInSeconds / 60);
	const seconds = Math.floor(timeInSeconds % 60);
	return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const TextWrapper = ({ children }: { children: React.ReactNode }) => {
	return (
		<Text size="small" align="center">
			<Box xcss={styles.codeStyledText}>{children}</Box>
		</Text>
	);
};

export const AudioTimeIndicator = (): React.JSX.Element => {
	const { audioRef } = useAudioPlayerStreamingContext();
	const [currentTimeDisplay, setCurrentTimeDisplay] = useState(DEFAULT_TIME_DISPLAY);
	const [totalTimeDisplay, setTotalTimeDisplay] = useState(DEFAULT_TIME_DISPLAY);

	const isAudioDurationUnknown = totalTimeDisplay === DEFAULT_TIME_DISPLAY;

	const handleTimeUpdate = useCallback(() => {
		const audioEl = audioRef.current;
		if (!audioEl) {
			return;
		}

		const currentTime = Math.floor(audioEl.currentTime ?? 0);
		setCurrentTimeDisplay(formatTime(currentTime));
	}, [audioRef]);

	const handleDurationChange = useCallback(() => {
		const audioEl = audioRef.current;
		if (!audioEl) {
			return;
		}

		const duration = audioEl.duration;
		if (Number.isFinite(duration)) {
			setTotalTimeDisplay(formatTime(duration));
		} else {
			setTotalTimeDisplay(DEFAULT_TIME_DISPLAY);
		}
	}, [audioRef]);

	// Reset displays when audio source is cleared (during fetch)
	useEffect(() => {
		const audioEl = audioRef.current;
		if (!audioEl) {
			return;
		}

		const handleEmptied = () => {
			setCurrentTimeDisplay(DEFAULT_TIME_DISPLAY);
			setTotalTimeDisplay(DEFAULT_TIME_DISPLAY);
		};

		audioEl.addEventListener('emptied', handleEmptied);

		return () => {
			audioEl.removeEventListener('emptied', handleEmptied);
		};
	}, [audioRef]);

	useEffect(() => {
		const audioEl = audioRef.current;
		if (!audioEl) {
			return;
		}

		handleTimeUpdate();
		handleDurationChange();

		audioEl.addEventListener('timeupdate', handleTimeUpdate);
		audioEl.addEventListener('durationchange', handleDurationChange);

		return () => {
			audioEl.removeEventListener('timeupdate', handleTimeUpdate);
			audioEl.removeEventListener('durationchange', handleDurationChange);
		};
	}, [audioRef, handleTimeUpdate, handleDurationChange]);

	return (
		<Grid
			testId="audio-time-indicator"
			columnGap="space.050"
			alignItems="center"
			xcss={styles.grid}
		>
			<TextWrapper>{currentTimeDisplay}</TextWrapper>

			<TextWrapper>/</TextWrapper>

			{isAudioDurationUnknown ? (
				<Suspense fallback={null}>
					<GeneratingAnimation />
				</Suspense>
			) : (
				<TextWrapper>{totalTimeDisplay}</TextWrapper>
			)}
		</Grid>
	);
};
